import { panelClass } from "@/components/ui/modal-panel";
import { Note } from "@/lib/types";
import { Pin, Pencil, Trash2, Share2, X } from "lucide-react";

interface Props {
  note: Note;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onTogglePin: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onShare: () => void;
  onDismissShare: () => void;
  isPinned: boolean;
  creatorLabel: string;
  sharerLabel: string;
  accessLabel: string;
  isOwner: boolean;
  projectMeta: any;
  isHalfWorkspaceWidth: boolean;
  noteCardGridColsClass: string;
  noteMetaGapClass: string;
}

export default function NoteCard({
  note,
  isExpanded,
  onToggleExpand,
  onTogglePin,
  onEdit,
  onDelete,
  onShare,
  onDismissShare,
  isPinned,
  creatorLabel,
  isOwner,
  projectMeta,
}: Props) {

  const clientName = projectMeta?.client ?? "";
  const projectDisplayName = projectMeta?.project ?? note.project_name ?? "";
  const location = projectMeta?.location ?? "";

  const theme = {
    border: "border-purple-500/40",
    glow: "shadow-[0_8px_25px_rgba(0,0,0,0.45)]",
    gradient: "bg-black/70",
  };

  const createdDate = note.created_at
    ? new Date(note.created_at).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "";

  return (
    <div
      className={`
        relative
        rounded-2xl
        border
        ${panelClass}
        transition-[box-shadow,transform,background-color,border-color]
        duration-200
        hover:-translate-y-[2px]
        hover:shadow-[0_12px_35px_rgba(123,46,230,0.35)]
        cursor-pointer
        flex
        flex-col
        h-full
        min-h-[220px]
      `}
      onClick={onToggleExpand}
      role="button"
      tabIndex={0}
      aria-expanded={isExpanded}
      aria-label={`Note: ${note.title || "Untitled"}`}
    >

      {/* Toolbar */}
      <div className="absolute right-2 top-2 flex items-center gap-0.5">

        {note.access?.kind === "shared" && note.access?.share_id && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDismissShare(); }}
            className="p-0.5 rounded hover:text-red-800 transition"
          >
            <X size={15} />
          </button>
        )}

        {isOwner && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onShare(); }}
            className="p-0.5 rounded hover:text-orange-800 transition"
          >
            <Share2 size={15} />
          </button>
        )}

        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onTogglePin(); }}
          className={`p-0.5 rounded transition ${
            isPinned ? "text-yellow-400" : "hover:text-yellow-300"
          }`}
        >
          <Pin size={15} />
        </button>

        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onEdit(); }}
          className="p-0.5 rounded hover:text-green-800 transition"
        >
          <Pencil size={15} />
        </button>

        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="p-0.5 rounded hover:text-red-800 transition"
        >
          <Trash2 size={15} />
        </button>
      </div>

      {/* INNER FLEX WRAPPER (critical fix) */}
      <div className="flex flex-col h-full p-5">

        {/* HEADER */}
        <div className="space-y-1 pr-10">
          <div className="text-base font-semibold tracking-tight">
            {note.title || "Untitled"}
          </div>

          {clientName ? (
            <div className="text-[11px] text-purple-300 uppercase tracking-wide leading-snug">
              <div>{clientName}</div>
              {(location || projectDisplayName) && (
                <div>
                  {location || "-"}
                  {projectDisplayName ? ` - ${projectDisplayName}` : ""}
                </div>
              )}
            </div>
          ) : projectDisplayName ? (
            <div className="text-[11px] text-purple-300 uppercase tracking-wide leading-snug">
              {projectDisplayName}
            </div>
          ) : null}
        </div>

        {/* Divider */}
        <div className="my-3 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

        {/* CONTENT AREA (flex grows) */}
        <div className="flex-1 flex flex-col">
          <div
            className={`text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap ${
              isExpanded ? "" : "line-clamp-3"
            }`}
          >
            {note.content}
          </div>
        </div>

        {/* FOOTER (now glued) */}
        <div className="mt-auto pt-3 border-t border-white/5 flex items-end justify-between text-[11px]">
          <div className="text-white/50 font-medium tracking-wide">
            {createdDate}
          </div>
          <div className="text-white/70 font-semibold">
            {creatorLabel}
          </div>
        </div>

      </div>
    </div>
  );
}
